import api from '../service/api.service';

import {
  ActiveSortField,
  ClientData,
  FilterTypes,
  PaginationQueryParams,
  ResultSet,
} from '../../types';
import { parseClientBankingFilters, parseQueryFilters } from '../../helpers/parseQueryFilters';
import { parseQuerySortFields } from '../../helpers/parseQuerySortFields';
import {
  CreatePayoutBankAccountPayload,
  CreatePayoutBankAccountResponse,
  CreatePayoutMailingAddressPayload,
  CreatePayoutMailingAddressResponse,
  PayoutChangeHistoryResponse,
  PayoutDetailsResponse,
  PayoutMethod,
} from '../../views/ClientsView/clientPayoutDetails/types';

const BASE_URL_V1 = '/v1/clients';
const CUSTOMER_ONBOARDING_BASE_URL = '/v1/customer-onboarding';
const accountingSystem = localStorage.getItem('accountingSystem');

export type OnboardingAuthTypeResponse = {
  authType: string;
};

export type OnboardingDefaultConfigResponse = {
  linkDeliveryChannel: string | null;
  otpChannel: string | null;
  authType: string | null;
};

export type ClientAutoGenerateConfigResponse = {
  autoGenerateClientEmail: boolean;
  autoGenerateClientAddress: boolean;
};

export type UserIdAvailabilityResponse = {
  userId: string;
  normalizedUserId: string;
  available: boolean;
  customerId?: number;
};

export type LinkedClientAccountResponse = {
  id: number;
  customerId: number | null;
  referenceId: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  email: string;
  userId: string | null;
  status: string;
  updatedAt: string;
};

export const getClientAutoGenerateConfig = async () => {
  return await api.get<ClientAutoGenerateConfigResponse>(`${BASE_URL_V1}/autogenerate-config`);
};

export const createClientRecord = async (data: unknown) => {
  return await api.post(BASE_URL_V1, data);
};

export const getClientsList = async ({
  size,
  page,
  count,
  match,
  filters,
  activeSortField,
}: PaginationQueryParams) => {
  const queryFilters = parseQueryFilters(filters);
  const sortFilters = parseQuerySortFields(activeSortField as ActiveSortField);

  const targetUrl = `${BASE_URL_V1}?size=${size}&page=${page}&count=${count}&match=${match || ''}${
    queryFilters || ''
  }${sortFilters}&includeAdditionalInfo=true`;

  return await api.get<ResultSet<ClientData>>(targetUrl);
};

export const getClientRecord = async (id: number | string) => {
  const targetUrl = `${BASE_URL_V1}/${id}`;

  return await api.get<ClientData>(targetUrl);
};

export const updateClientRecord = async (clientId: string, data: unknown) => {
  const targetUrl = `${BASE_URL_V1}/${clientId}`;

  return await api.put(targetUrl, data);
};

export const resetClientAccount = async (clientId: string) => {
  const targetUrl = `${BASE_URL_V1}/${clientId}/invite`;

  return await api.put(targetUrl, null);
};

export const getClientBankingInfo = async (clientId: string) => {
  const targetUrl = `${BASE_URL_V1}/${clientId}/banking`;

  return await api.get(targetUrl);
};

export const startClientProvisioning = async (clientId: string) => {
  const targetUrl = `${BASE_URL_V1}/${clientId}/banking/start-provisioning`;

  return await api.put(targetUrl);
};

export const getPlaintiffConsentDocument = async (token: string, documentKey: string) => {
  const targetUrl = `${BASE_URL_V1}/confirmation/${token}/document-url/${documentKey}`;
  return await api.get(targetUrl);
};

export const getClientTransactionList = async (
  { size, page, filters, activeSortField }: PaginationQueryParams,
  clientId: string,
) => {
  const queryFilters = parseClientBankingFilters(filters as FilterTypes);
  const sortFilters = parseQuerySortFields(activeSortField as ActiveSortField);

  const targetUrl = `${BASE_URL_V1}/${clientId}/banking/ledger?size=${size}&page=${page}${
    queryFilters || ''
  }${sortFilters}`;

  return await api.get(targetUrl);
};

export const getClientBirthDate = async (id: number) => {
  const targetUrl = `${BASE_URL_V1}/${id}/birthdate`;

  return await api.get<{ birthdate: string; id: number }>(targetUrl);
};

export const getClientSSN = async (id: number) => {
  const targetUrl = `${BASE_URL_V1}/${id}/ssn`;

  return await api.get<{ id: number; ssn: string }>(targetUrl);
};

export const deletePlaintiffAccount = async (token: string, email: string) => {
  const targetUrl = `${BASE_URL_V1}/self-remove-from-email`;

  return await api.get<void>(targetUrl, {
    params: {
      token,
      email,
    },
  });
};

export const sendEmailConfirmation = async (id: number) => {
  const targetUrl = `${BASE_URL_V1}/${id}/send-email-confirmation`;

  return await api.put<{ id: number; ssn: string }>(targetUrl);
};

export const setClientExternalVendorId = async (clientId: number, externalVendorId: string) => {
  const targetUrl = `${BASE_URL_V1}/${clientId}/${accountingSystem}/accounting-party-id`;
  return await api.put<{
    isSynced: boolean;
    status: string;
    externalVendorId: string | null;
    lastSyncedAt: string | null;
    link: string | null;
  }>(targetUrl, { externalVendorId });
};

export const removeClientExternalVendorId = async (clientId: number) => {
  const targetUrl = `${BASE_URL_V1}/${clientId}/${accountingSystem}/accounting-party-id`;
  return await api.delete<{ message: string; success: boolean }>(targetUrl);
};

export const getClientDataByEmailConfirmationToken = async (token: string) => {
  const targetUrl = `${BASE_URL_V1}/email-confirmations/${token}`;

  return await api.get(targetUrl);
};

export const confirmClientEmail = async (data: unknown) => {
  return await api.post(`${BASE_URL_V1}/confirm-email`, data);
};

export const updateClientPersonalInformation = async (
  id: number,
  data: { ssn: string; birthdate: Date | null | string },
) => {
  const targetUrl = `${BASE_URL_V1}/${id}/ssn-dob`;

  return await api.put(targetUrl, {
    ...data,
  });
};

export const getOnboardingAuthType = async () => {
  const targetUrl = `${BASE_URL_V1}/onboarding/auth-type`;

  return await api.get<OnboardingAuthTypeResponse>(targetUrl);
};

export const getOnboardingDefaultConfig = async () => {
  const targetUrl = `${BASE_URL_V1}/onboarding/default-config`;

  return await api.get<OnboardingDefaultConfigResponse>(targetUrl);
};

export const reInitiateCustomerOnboarding = async (customerId: number, settlementId?: number) => {
  const targetUrl = `${CUSTOMER_ONBOARDING_BASE_URL}/re-initiate`;

  return await api.post(targetUrl, {
    customerId,
    ...(settlementId !== undefined && { settlementId }),
  });
};

export const reInitiateCustomerOnboardingSignal = async (
  clientId: number,
  settlementId?: number,
) => {
  const targetUrl = `${CUSTOMER_ONBOARDING_BASE_URL}/admin/re-initiate/signal`;

  return await api.post(targetUrl, {
    clientId,
    ...(settlementId !== undefined && { settlementId }),
  });
};

export const checkClientUserIdAvailability = async (params: {
  userId: string;
  customerId?: number;
}) => {
  return await api.get<UserIdAvailabilityResponse>(`${BASE_URL_V1}/user-id-availability`, {
    params,
  });
};

export const getLinkedClientAccounts = async (clientId: number | string) => {
  return await api.get<LinkedClientAccountResponse[]>(`${BASE_URL_V1}/${clientId}/linked-accounts`);
};

// ─── Payout Details ───────────────────────────────────────────────────────────

export const getPayoutDetails = async (clientId: number | string) =>
  api.get<PayoutDetailsResponse>(`${BASE_URL_V1}/${clientId}/payout-details`);

export const getPayoutChangeHistory = async (
  clientId: number | string,
  params: {
    page?: number;
    size?: number;
    paymentMethod?: 'ACH' | 'CHECK';
    count?: boolean;
    settlementId?: number;
  },
) => {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.size) query.set('size', String(params.size));
  if (params.paymentMethod) query.set('paymentMethod', params.paymentMethod);
  if (params.count) query.set('count', 'true');
  if (params.settlementId !== undefined) query.set('settlementId', String(params.settlementId));
  return api.get<PayoutChangeHistoryResponse>(
    `${BASE_URL_V1}/${clientId}/payout-change-history?${query.toString()}`,
  );
};

export const createPayoutAch = async (
  clientId: number | string,
  data: {
    accountHolderName: string;
    accountType: string;
    routingNumber: string;
    accountNumber: string;
    payoutMethod: PayoutMethod;
    sendEmailNotification: boolean;
    reasonCode?: string;
    note?: string;
  },
  settlementId?: number,
) =>
  api.post<void>(`${BASE_URL_V1}/${clientId}/payout-details`, {
    ...data,
    ...(settlementId !== undefined && { settlementId }),
  });

export const updatePayoutAch = async (
  clientId: number | string,
  data: {
    accountHolderName: string;
    accountType: string;
    routingNumber: string;
    accountNumber?: string;
    payoutMethod: PayoutMethod;
    reasonCode: string;
    note: string;
    sendEmailNotification: boolean;
  },
  settlementId?: number,
) =>
  api.put<void>(`${BASE_URL_V1}/${clientId}/payout-details`, {
    ...data,
    ...(settlementId !== undefined && { settlementId }),
  });

export const createPayoutCheck = async (
  clientId: number | string,
  data: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    zip: string;
    phoneNumber: string;
    payoutMethod: PayoutMethod;
    sendEmailNotification: boolean;
    reasonCode?: string;
    note?: string;
  },
  settlementId?: number,
) =>
  api.post<void>(`${BASE_URL_V1}/${clientId}/payout-details`, {
    ...data,
    ...(settlementId !== undefined && { settlementId }),
  });

export const updatePayoutCheck = async (
  clientId: number | string,
  data: {
    line1: string;
    line2?: string | null;
    city: string;
    state: string;
    zip: string;
    phoneNumber: string;
    payoutMethod: PayoutMethod;
    reasonCode: string;
    note: string;
    sendEmailNotification: boolean;
  },
  settlementId?: number,
) =>
  api.put<void>(`${BASE_URL_V1}/${clientId}/payout-details`, {
    ...data,
    ...(settlementId !== undefined && { settlementId }),
  });

export const updatePayoutMode = async (
  clientId: number | string,
  data: { preferredPayoutMode: string; reasonCode: string; note?: string },
) => api.put<void>(`${BASE_URL_V1}/${clientId}/payout-details/mode`, data);

export const createPayoutBankAccount = async (
  clientId: number,
  payload: CreatePayoutBankAccountPayload,
) => {
  const targetUrl = `${BASE_URL_V1}/${clientId}/payout-details/bank-account`;
  return await api.post<CreatePayoutBankAccountResponse>(targetUrl, payload);
};

export const createPayoutMailingAddress = async (
  clientId: number,
  payload: CreatePayoutMailingAddressPayload,
) => {
  const targetUrl = `${BASE_URL_V1}/${clientId}/payout-details/mailing-address`;
  return await api.post<CreatePayoutMailingAddressResponse>(targetUrl, payload);
};
